import { Component, createRef } from "react";
import AceEditor from "react-ace";

/**
 * AceEditor that re-measures when its container's LAYOUT size changes.
 *
 * Ace measures its width once at mount and caches the wrap column and character metrics derived from
 * it; nothing re-measures afterwards. A playground widget's card is sized by its `width` port, so
 * resizing a widget leaves Ace rendering at the old width — too narrow for the card (content sitting
 * in a column with dead space beside it) or too wide (content spilling past the card edge and forcing
 * the body to scroll). Both are the same stale cache seen from opposite sides.
 *
 * NOT a fix for the selection drift under canvas zoom. That is the same cache going stale, but via a
 * CSS transform, which changes the rendered scale WITHOUT changing any layout box — so no resize is
 * observed and this does nothing for it. Keeping the two apart matters: this one is a real fix for a
 * real symptom, and it must not be mistaken for having addressed the other.
 */
export default class AceView extends Component {
  constructor(props) {
    super(props);
    this.containerRef = createRef();
    this.editor = null;
    this.resizeObserver = null;
  }

  componentWillUnmount() {
    this.disconnectResizeObserver();
  }

  disconnectResizeObserver() {
    if ( this.resizeObserver ) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  handleLoad = (editor) => {
    this.editor = editor;
    if ( this.props.onLoad ) {
      this.props.onLoad(editor);
    }
    this.disconnectResizeObserver();
    const node = this.containerRef.current;
    if ( !node || typeof ResizeObserver === "undefined" ) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      // `true` forces a full recompute instead of trusting the cached size.
      if ( this.editor ) this.editor.resize(true);
    });
    this.resizeObserver.observe(node);
  };

  render() {
    const { onLoad, ...aceProps } = this.props;
    return (
      <div ref={this.containerRef}>
        <AceEditor {...aceProps} onLoad={this.handleLoad} />
      </div>
    );
  }
}
